"use client";

import {
 createContext,
 use,
 useMemo,
 type ReactNode,
} from "react";
import {
 useMapState,
 type MapState,
 type ViewportCenter,
} from "@/hooks/use-map-state";

export type { ViewportCenter };

interface MapInteractionContextValue extends Omit<
 MapState,
 "viewportBounds" | "setViewportBounds"
> {
 mapInteractionEnabled: boolean;
 setMapInteractionEnabled: (enabled: boolean) => void;
 optimizedZoom: number | null;
 setOptimizedZoom: (zoom: number | null) => void;
}

const MapInteractionContext = createContext<MapInteractionContextValue | null>(
 null,
);

export function MapInteractionProvider({ children }: { children: ReactNode }) {
 const mapState = useMapState();

 const value = useMemo(
  () => ({
   hoveredProductId: mapState.hoveredProductId,
   setHoveredProductId: mapState.setHoveredProductId,
   selectedProductId: mapState.selectedProductId,
   setSelectedProductId: mapState.setSelectedProductId,
   viewportCenter: mapState.viewportCenter,
   setViewportCenter: mapState.setViewportCenter,
   zoom: mapState.zoom,
   setZoom: mapState.setZoom,
   isInteracting: mapState.isInteracting,
   setIsInteracting: mapState.setIsInteracting,
   clearSelection: mapState.clearSelection,
   mapInteractionEnabled: mapState.isInteracting,
   setMapInteractionEnabled: mapState.setIsInteracting,
   optimizedZoom: mapState.zoom,
   setOptimizedZoom: mapState.setZoom,
  }),
  [mapState],
 );

 return (
  <MapInteractionContext value={value}>
   {children}
  </MapInteractionContext>
 );
}

const defaultMapInteractionContext: MapInteractionContextValue = {
 hoveredProductId: null,
 setHoveredProductId: () => {},
 selectedProductId: null,
 setSelectedProductId: () => {},
 viewportCenter: null,
 setViewportCenter: () => {},
 zoom: null,
 setZoom: () => {},
 isInteracting: false,
 setIsInteracting: () => {},
 clearSelection: () => {},
 mapInteractionEnabled: false,
 setMapInteractionEnabled: () => {},
 optimizedZoom: null,
 setOptimizedZoom: () => {},
};

export function useMapInteractionContext() {
 const context = use(MapInteractionContext);
 return context ?? defaultMapInteractionContext;
}

export { MapInteractionContext };
